import { Alert, CircularProgress } from "@mui/material";
import * as React from "react";
import { Link, useParams } from "react-router-dom";

import { ReadOnlyAIAgentThreadProvider } from "@/Admin/ExploreAgentThreads/ReadOnlyAIAgentThreadProvider";
import { AgentThread } from "@/AgentThread/AgentThreadSidebar/AgentThread";
import { AgentThreadByIdView } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView";
import { AIAgentThreadProvider } from "@/AgentThread/AgentThreadSidebar/AIAgentThreadContext";
import { useNavigation } from "@/providers";
import { useAuth } from "@/providers/auth/hooks";
import { PageBreadcrumbs, PageLayout } from "@/shared/components";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { useAgentThread } from "@/shared/domain/agents/hooks";
import { RobotoDomainException } from "@/shared/http";
import { Goto } from "@/shared/navigation";

import styles from "./AgentThreadDetail.module.css";

/**
 * Detail page for an agent thread at ``/agents/threads/:threadId``.
 *
 * Mirrors :class:`AdminAgentThreadViewer` for non-admin users. Branches on
 * ownership inside :func:`AgentThreadDetailContent`:
 *
 * - **Owner** (``agentThread.created_by`` matches the signed-in user): render
 *   the interactive ``<AgentThread />`` so the user can continue the
 *   conversation.
 * - **Non-owner** (a session visible to the user but created by a
 *   teammate): render ``<AgentThreadByIdView />`` inside
 *   ``ReadOnlyAIAgentThreadProvider``. Writes are no-ops; the user can fork
 *   from any assistant message to land on their own copy.
 *
 * Loading and error states flow through ``<React.Suspense>`` and
 * ``<ErrorBoundary>`` rather than inline ``isLoading`` / ``error``
 * branching. The thread fetch lives inside :func:`AgentThreadDetailContent`
 * (and :func:`AgentThreadTitleSuffix` for the breadcrumb title), both
 * of which call :func:`useAgentThread` and suspend until the response
 * is available. A 4xx response surfaces the server's message; anything
 * else falls back to a generic copy so we don't leak server-side
 * details.
 */
export function AgentThreadDetail() {
  const { threadId } = useParams<{ threadId: string }>();

  if (threadId === undefined) {
    // Programming error — the route declaration registers
    // ``/agents/threads/:threadId`` so React Router guarantees the
    // param is set by the time this component mounts.
    throw new Error("threadId not provided");
  }

  return (
    <PageLayout
      title={`Roboto - Agent thread - ${threadId}`}
      headerContentLeft={
        // The fallback omits the title suffix so the breadcrumb is
        // stable before the thread resolves. Once the suspense boundary
        // unblocks, the rendered breadcrumb appends ``- ${title}`` when
        // the thread has one.
        <React.Suspense
          fallback={<AgentThreadBreadcrumb threadId={threadId} />}
        >
          <AgentThreadBreadcrumb threadId={threadId} includeTitle />
        </React.Suspense>
      }
    >
      <ErrorBoundary
        fallback={({ error }) => <AgentThreadErrorAlert error={error} />}
      >
        <React.Suspense fallback={<AgentThreadLoading />}>
          <AgentThreadDetailContent threadId={threadId} />
        </React.Suspense>
      </ErrorBoundary>
    </PageLayout>
  );
}

interface AgentThreadBreadcrumbProps {
  threadId: string;
  /**
   * When true, suspends on :func:`useAgentThread` and appends ``-
   * ${title}`` to the thread id when the loaded record has a title.
   * Defaults to false so the same component can be reused as the
   * Suspense fallback without triggering a second network request or
   * re-suspending.
   */
  includeTitle?: boolean;
}

function AgentThreadBreadcrumb({
  threadId,
  includeTitle = false,
}: AgentThreadBreadcrumbProps) {
  return (
    <PageBreadcrumbs>
      <Link to={Goto.route.AgentThreads}>Agent Threads</Link>
      <span>
        {threadId}
        {includeTitle && <AgentThreadTitleSuffix threadId={threadId} />}
      </span>
    </PageBreadcrumbs>
  );
}

function AgentThreadTitleSuffix({ threadId }: { threadId: string }) {
  const { data: thread } = useAgentThread({ threadId });
  if (!thread.title) {
    return null;
  }
  return <> - {thread.title}</>;
}

function AgentThreadLoading() {
  return (
    <div className={styles.loadingContainer}>
      <CircularProgress size="1.5rem" />
    </div>
  );
}

function AgentThreadErrorAlert({ error }: { error: unknown }) {
  // 4xx errors are user-actionable (session not found, forbidden, etc.)
  // — surface the server's message verbatim. Anything else (5xx,
  // network failures, framework errors) gets the generic copy so we
  // don't leak server-side details and the user has a contact path.
  if (error instanceof RobotoDomainException && error.isClientError()) {
    return <Alert severity="error">{error.message}</Alert>;
  }
  return (
    <Alert severity="error">
      Failed to load session. Please contact{" "}
      <a href="mailto:support@roboto.ai">support@roboto.ai</a> if this problem
      persists.
    </Alert>
  );
}

function AgentThreadDetailContent({ threadId }: { threadId: string }) {
  const { goto } = useNavigation();
  const { currentUser } = useAuth();
  const { data: thread } = useAgentThread({ threadId });

  // Hold the owner/non-owner decision until the signed-in user is
  // loaded. ``currentUser`` is ``null`` while authentication is still
  // resolving, so checking ``thread.created_by`` against it too early
  // would briefly flash the read-only viewer on the user's own
  // thread.
  const ownerCheckResolved = currentUser !== null;
  const isOwnThread =
    ownerCheckResolved && thread.created_by === currentUser.user_id;

  // Keep the URL in sync when the active thread id changes from inside
  // the interactive branch — e.g. when the user starts a brand-new
  // conversation from this page instead of continuing the loaded
  // thread. Without this, reload and the browser back button would
  // point at the previous thread.
  const handleThreadIdChanged = React.useCallback(
    (nextId: string | undefined) => {
      if (nextId && nextId !== thread.session_id) {
        goto.agentThread({ threadId: nextId });
      }
    },
    [thread.session_id, goto],
  );

  // When the user forks a read-only thread, navigate this page to the
  // fork's detail URL so they land on their own copy and can continue
  // the conversation there.
  const handleForkNavigated = React.useCallback(
    (nextId: string) => {
      goto.agentThread({ threadId: nextId });
    },
    [goto],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.metadata}>
          {/* Thread id renders before title so it's the first thing a
              user can copy when grabbing context for a bug report or
              support thread. */}
          <div className={styles.metadataItem}>
            <span className={styles.metadataLabel}>Thread ID:</span>
            <span className={styles.metadataValue}>{thread.session_id}</span>
          </div>
          {thread.title && (
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>Title:</span>
              <span className={styles.metadataValue}>{thread.title}</span>
            </div>
          )}
          <div className={styles.metadataItem}>
            <span className={styles.metadataLabel}>Created by:</span>
            <span className={styles.metadataValue}>{thread.created_by}</span>
          </div>
          <div className={styles.metadataItem}>
            <span className={styles.metadataLabel}>Created:</span>
            <span className={styles.metadataValue}>
              {new Date(thread.created).toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.agentThreadContent}>
        {ownerCheckResolved && isOwnThread && (
          // Keying ``AIAgentThreadProvider`` by ``session_id`` forces a remount
          // when the user navigates between their own threads. The
          // provider captures ``initialThreadId`` in a lazy state
          // initializer that runs only at mount, so swapping the prop
          // alone would not change the active thread — a remount is the
          // supported path.
          <AIAgentThreadProvider
            key={thread.session_id}
            initialThreadId={thread.session_id}
            onThreadIdChanged={handleThreadIdChanged}
          >
            <AgentThread />
          </AIAgentThreadProvider>
        )}
        {ownerCheckResolved && !isOwnThread && (
          <ReadOnlyAIAgentThreadProvider
            onAgentThreadClicked={handleForkNavigated}
          >
            <AgentThreadByIdView agentThread={thread} />
          </ReadOnlyAIAgentThreadProvider>
        )}
      </div>

      {ownerCheckResolved && !isOwnThread && (
        <div className={styles.readOnlyBanner}>
          Read-only view of someone else&rsquo;s thread. Fork from any assistant
          message to continue the conversation in your own thread.
        </div>
      )}
    </div>
  );
}
